import { useCallback, useMemo, useState } from 'react';

import type { WelfareFishCandidate } from '@scaleaq/sitecontrol-lib';

import { useFishCropImages } from '@restDataAccess/orbitOneGateway';
import { FlexAligner } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/plugins/counter.css';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

import { responsive } from '@/utils';

import { FishObservationCaption } from './fishObservationGallery/FishObservationCaption.tsx';
import { liceFilterClasses } from './fishObservationGallery/fishObservationGallery.constants.ts';
import {
  isLiceFilterClass,
  padImageNumber,
  resolveCropPixelSize,
  resolveFullResolutionState,
  resolveImageStatus,
  toGalleryItems,
} from './fishObservationGallery/fishObservationGallery.functions.ts';
import {
  ImageGalleryStyledWrapper,
  StyledImage,
  StyledImageCard,
} from './fishObservationGallery/fishObservationGallery.style.ts';
import {
  FishObservationSlide,
  OrbitOneGalleryFilterClass,
} from './fishObservationGallery/fishObservationGallery.types.ts';
import { FishObservationGalleryEmptyState } from './fishObservationGallery/FishObservationGalleryEmptyState.tsx';
import { FishObservationSlideView } from './fishObservationGallery/FishObservationSlideView.tsx';
import { ImageHeading } from './fishObservationGallery/ImageHeading.tsx';
import { useFullResolutionPrefetch } from './fishObservationGallery/useFullResolutionPrefetch.ts';

const lightboxPlugins = [Zoom, Thumbnails, Counter, Fullscreen];

interface FishObservationGalleryProps {
  observations: WelfareFishCandidate[];
  /** Selected filter classes; empty means no filter is active. */
  filters: OrbitOneGalleryFilterClass[];
  availableFilterClasses: OrbitOneGalleryFilterClass[];
  isLoading: boolean;
}

/**
 * Grid of fish-crop thumbnails for a day's lice observations. Clicking a thumbnail opens a
 * fullscreen lightbox with zoom, previous/next navigation, a thumbnail strip, and a metadata footer
 * (capture time, depth, weight, lice detections). Crops are fetched as authenticated object URLs.
 */
export const FishObservationGallery = ({
  observations,
  filters,
  availableFilterClasses,
  isLoading,
}: FishObservationGalleryProps) => {
  const { t } = useTranslation('orbitOne');
  // -1 means the lightbox is closed; otherwise it is the open slide index.
  const [index, setIndex] = useState(-1);

  const cameraFilterType = availableFilterClasses.some(isLiceFilterClass) ? 'lice' : 'welfare';
  // The lice gallery draws every lice box on the fish; the chips only choose which fish are listed.
  const annotationTypes = cameraFilterType === 'lice' ? liceFilterClasses : filters;

  const galleryItems = useMemo(
    () => toGalleryItems({ observations, cameraFilterType }),
    [observations, cameraFilterType]
  );

  const galleryPaths = useMemo(() => galleryItems.map(item => item.path), [galleryItems]);
  const { urls: thumbnailUrls, statusByPath: thumbnailStatusByPath } = useFishCropImages({
    paths: galleryPaths,
    request: { size: 'thumb', format: 'webp' },
  });

  const thumbnailsReady =
    galleryPaths.length > 0 &&
    galleryPaths.every(path => thumbnailStatusByPath[path] !== 'loading');

  const {
    urls: fullResolutionUrls,
    statusByPath: fullResolutionStatusByPath,
    prioritizePath,
  } = useFullResolutionPrefetch({
    galleryPaths,
    thumbnailsReady,
  });

  const viewItems = useMemo(
    () =>
      galleryItems.map(({ observation, path }, itemIndex) => {
        const [width, height] = resolveCropPixelSize({ observation, cameraFilterType }) ?? [];
        const fullResolutionSource = fullResolutionUrls[path];
        const source = fullResolutionSource ?? thumbnailUrls[path];
        return {
          path,
          src: source ?? '',
          alt: t('liceCount.imageGallery.imageAlt', { number: padImageNumber(itemIndex + 1) }),
          width,
          height,
          observation,
          fetchingStatus: resolveImageStatus({ source, status: thumbnailStatusByPath[path] }),
          fullResolutionState: resolveFullResolutionState({
            fullResolutionSource,
            fullResolutionStatus: fullResolutionStatusByPath[path],
          }),
        } satisfies FishObservationSlide & { path: string };
      }),
    [
      cameraFilterType,
      fullResolutionStatusByPath,
      fullResolutionUrls,
      galleryItems,
      thumbnailStatusByPath,
      thumbnailUrls,
      t,
    ]
  );

  const handleView = useCallback(
    (viewed: number) => {
      const path = viewItems[viewed]?.path;
      if (path) prioritizePath(path);
      setIndex(viewed);
    },
    [prioritizePath, viewItems]
  );

  if (!isLoading && galleryItems.length === 0) {
    return (
      <FishObservationGalleryEmptyState
        filters={filters}
        availableFilterClasses={availableFilterClasses}
      />
    );
  }

  return (
    <FlexAligner direction='column' gap={responsive.spacing.medium}>
      <ImageGalleryStyledWrapper>
        {viewItems.map((item, itemIndex) => (
          <StyledImageCard
            key={`${item.observation.trackId}-${item.observation.timestampMs}`}
            isLoading={!item.src && item.fetchingStatus === 'loading'}
            isError={item.fetchingStatus === 'error'}
            errorMessage={
              item.fetchingStatus === 'error' ? t('liceCount.imageGallery.cannotLoadImage') : ''
            }
            onClick={() => handleView(itemIndex)}
            aria-label={item.alt}
          >
            <ImageHeading observation={item.observation} />
            <FlexAligner fullHeight alignment='center'>
              {item.src && <StyledImage src={item.src} alt={item.alt} />}
            </FlexAligner>
          </StyledImageCard>
        ))}
      </ImageGalleryStyledWrapper>
      <Lightbox
        open={index >= 0}
        index={index < 0 ? 0 : index}
        close={() => setIndex(-1)}
        slides={viewItems}
        plugins={lightboxPlugins}
        zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
        on={{ view: ({ index: viewed }) => handleView(viewed) }}
        styles={{ icon: { color: 'inherit' } }}
        render={{
          slide: ({ slide, zoom }) => (
            <FishObservationSlideView
              slide={slide as FishObservationSlide}
              zoom={zoom}
              annotationTypes={annotationTypes}
            />
          ),
          slideFooter: () => {
            const item = viewItems[index];
            return item ? <FishObservationCaption observation={item.observation} /> : null;
          },
        }}
      />
    </FlexAligner>
  );
};
